import { createSelector } from 'reselect';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Selectors } from '@grandvisionhq/configurator';
import { uninitState, FeatureToggleSelectors } from '@grandvisionhq/state';
import { isNewPrescriptionAndUpload } from '../../utils/is-new-prescription-upload.js';
import { getLensTypes as getLensTypes$1, getPackages as getPackages$1, getLensAddOns } from '../bundles/selectors.js';
import { getPrescriptionType as getPrescriptionType$1, getPdUploadValidationRequirement as getPdUploadValidationRequirement$1, getSelectedLensType as getSelectedLensType$1, getSelectedPackage as getSelectedPackage$1, getSelectedAddOn as getSelectedAddOn$1, getPrescription as getPrescription$1, getAdditionalPrescriptionInfoFormState as getAdditionalPrescriptionInfoFormState$1, getManualPrescriptionValidationState as getManualPrescriptionValidationState$1, getPrescriptionUploadValidationState, getVerificationAllowed as getVerificationAllowed$1 } from '../configurations/selectors.js';
import { getLensTypeSku, getPackageSku } from '../configurations/selectors.internal.js';
import { convertToFrameInfo } from '../helpers.js';
import { getFrameImage as getFrameImage$1 } from '../products/selectors.js';
import { getFrames } from '../products/selectors.internal.js';
import { configuratorIdProp } from '../selectors.internal.js';
import { getConfigurator } from './selectors.internal.js';

/**
 * Exported selectors to be used in container components
 */ const getIsExtraValidationStepEnabled = createSelector(getConfigurator, (c)=>c.isExtraValidationStepEnabled
);
const getRxEntryTypes = createSelector(getConfigurator, (c)=>c.rxEntryTypes
);
const getBookAppointmentRoute = createSelector(getConfigurator, (c)=>c.bookAppointmentRoute
);
const getFrameSku = createSelector(getConfigurator, (c)=>{
    return c === null || c === void 0 ? void 0 : c.frameSku;
});
const getFrameImage = createSelector(getFrameSku, (s)=>s
, (_, props)=>props
, (frameSku, state, frameImageProps)=>{
    if (!frameSku) {
        return undefined;
    }
    const { width  } = frameImageProps;
    return getFrameImage$1(state, {
        frameSku,
        width
    });
});
/**
 * Returns the FrameInfo of the frame that's currently being configured
 */ const getSelectedFrame = createSelector(getFrameSku, getFrames, (frameSku, frames)=>{
    if (!frameSku) {
        return undefined;
    }
    const frame = frames[frameSku];
    if (!frame) {
        return undefined;
    }
    return convertToFrameInfo(frame, frameSku);
});
const getSelectedFrameProductType = createSelector(getFrameSku, getFrames, (frameSku, frames)=>{
    var ref1;
    if (!frameSku) {
        return;
    }
    const frame = frames[frameSku];
    if (!frame) {
        return;
    }
    return (ref1 = frame.productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name;
});
/**
 * Get the prescription type enum value
 */ const getPrescriptionType = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getPrescriptionType$1(state, {
        frameSku
    });
});
const getPdUploadValidationRequirement = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getPdUploadValidationRequirement$1(state, {
        frameSku
    });
});
/**
 * The currently selected lens-type product the frame that's being configured
 */ const getSelectedLensType = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedLensType$1(state, {
        frameSku
    });
});
/**
 * Returns the available lens-types for the frame that's currently being configured
 */ const getLensTypes = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    return getLensTypes$1(state, {
        frameSku
    });
});
/**
 * The currently selected package product for the frame that's being configured
 */ const getSelectedPackage = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedPackage$1(state, {
        frameSku
    });
});
const getPackages = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return uninitState();
    }
    return getPackages$1(state, {
        lensTypeSku
    });
});
/**
 * The currently selected add-on product for the frame that's being configured
 */ const getSelectedAddOn = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getSelectedAddOn$1(state, {
        frameSku
    });
});
const getAddOns = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const packageSku = getPackageSku(state, {
        frameSku
    });
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku || !packageSku) {
        return uninitState();
    }
    return getLensAddOns(state, {
        packageSku
    });
});
/**
 * The prescription parameter values as entered on the current frame / lens
 */ const getPrescription = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return uninitState();
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return uninitState();
    }
    return getPrescription$1(state, {
        frameSku,
        lensTypeSku
    });
});
/**
 * The prescription info as entered on the current frame / lens
 */ const getAdditionalPrescriptionInfoFormState = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return {};
    }
    return getAdditionalPrescriptionInfoFormState$1(state, {
        frameSku
    });
});
/**
 * Get the validation state of the manual prescription form
 */ const getManualPrescriptionValidationState = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku
    });
    if (!lensTypeSku) {
        return undefined;
    }
    return getManualPrescriptionValidationState$1(state, {
        frameSku,
        lensTypeSku
    });
});
/**
 * Get the validation state of the Prescription Step,
 * considering the currently selected prescriptionType
 */ const getIsPrescriptionUploadStepValid = createSelector(getFrameSku, getPrescriptionType, (s)=>s
, (frameSku, prescriptionType, state)=>{
    if (!frameSku) {
        return undefined;
    }
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        var ref2;
        return (ref2 = getPrescriptionUploadValidationState(state, {
            frameSku
        })) === null || ref2 === void 0 ? void 0 : ref2.isValid;
    }
    return undefined;
});
const getIsPrescriptionFormStepValid = createSelector(getFrameSku, getPrescriptionType, (s)=>s
, (frameSku, prescriptionType, state)=>{
    if (!frameSku) {
        return undefined;
    }
    if (CommerceToolsApi.isNewPrescription(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        var ref3;
        return (ref3 = getManualPrescriptionValidationState(state)) === null || ref3 === void 0 ? void 0 : ref3.isValid;
    }
    return undefined;
});
/**
 * Step related selectors
 */ const hasVisitedStepPrescription = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'prescription'
    })
);
const hasVisitedStepLensPackage = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'lensPackage'
    })
);
const hasVisitedStepAddOn = createSelector((s)=>s
, configuratorIdProp, (s, id)=>Selectors.hasVisitedStep(s, {
        id,
        stepName: 'lensAddOn'
    })
);
const getBundleMinimumSupplement = createSelector(FeatureToggleSelectors.getFeatureToggles, getSelectedLensType, getSelectedPackage, getSelectedAddOn, hasVisitedStepLensPackage, hasVisitedStepAddOn, (featureToggles, lensType, lensPackage, selectedAddOn, hasVisitedStepLensPackage1, hasVisitedStepAddOn1)=>{
    if (!(featureToggles === null || featureToggles === void 0 ? void 0 : featureToggles.isConfiguratorRxPriceFromEnabled)) {
        return undefined;
    }
    if (hasVisitedStepAddOn1 && selectedAddOn) {
        return undefined;
    }
    if (hasVisitedStepLensPackage1) {
        if (lensPackage === null || lensPackage === void 0 ? void 0 : lensPackage.areAddOnsOptional) {
            return undefined;
        }
        var ref5;
        return (ref5 = lensPackage === null || lensPackage === void 0 ? void 0 : lensPackage.minimumSupplement) !== null && ref5 !== void 0 ? ref5 : undefined;
    }
    var ref4;
    return (ref4 = lensType === null || lensType === void 0 ? void 0 : lensType.minimumSupplement) !== null && ref4 !== void 0 ? ref4 : undefined;
});
/**
 * Calculates the total price and total discounted price for the current configuration
 */ const getBundlePrice = createSelector(getSelectedFrame, getSelectedLensType, getSelectedPackage, hasVisitedStepLensPackage, getSelectedAddOn, hasVisitedStepAddOn, getBundleMinimumSupplement, (frame, lensType, lensPackage, hasVisitedStepLensPackage2, addOn, hasVisitedStepAddOn2, bundleMinimumSupplement)=>{
    var ref30, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13, ref14, ref15, ref16;
    if (!frame || !frame.price) {
        return undefined;
    }
    var ref17, ref18, ref19, ref20;
    const bundlePrices = [
        frame.price.originalPrice,
        (ref17 = lensType === null || lensType === void 0 ? void 0 : (ref30 = lensType.price) === null || ref30 === void 0 ? void 0 : ref30.originalPrice) !== null && ref17 !== void 0 ? ref17 : 0,
        hasVisitedStepLensPackage2 ? (ref18 = lensPackage === null || lensPackage === void 0 ? void 0 : (ref6 = lensPackage.price) === null || ref6 === void 0 ? void 0 : ref6.originalPrice) !== null && ref18 !== void 0 ? ref18 : 0 : 0,
        hasVisitedStepAddOn2 ? (ref19 = addOn === null || addOn === void 0 ? void 0 : (ref7 = addOn.price) === null || ref7 === void 0 ? void 0 : ref7.originalPrice) !== null && ref19 !== void 0 ? ref19 : 0 : 0,
        (ref20 = bundleMinimumSupplement === null || bundleMinimumSupplement === void 0 ? void 0 : bundleMinimumSupplement.originalPrice) !== null && ref20 !== void 0 ? ref20 : 0, 
    ];
    const originalPrice = bundlePrices.reduce((sum, current)=>sum + current
    , 0);
    const hasDiscounts = [
        frame.price.discountedPrice,
        lensType === null || lensType === void 0 ? void 0 : (ref8 = lensType.price) === null || ref8 === void 0 ? void 0 : ref8.discountedPrice,
        hasVisitedStepLensPackage2 && (lensPackage === null || lensPackage === void 0 ? void 0 : (ref9 = lensPackage.price) === null || ref9 === void 0 ? void 0 : ref9.discountedPrice),
        hasVisitedStepAddOn2 && (addOn === null || addOn === void 0 ? void 0 : (ref10 = addOn.price) === null || ref10 === void 0 ? void 0 : ref10.discountedPrice),
        bundleMinimumSupplement === null || bundleMinimumSupplement === void 0 ? void 0 : bundleMinimumSupplement.discountedPrice, 
    ].filter((p)=>typeof p === 'number' && !isNaN(p)
    ).length > 0;
    var _discountedPrice, ref21, ref22, ref23, ref24, ref25, ref26, ref27, ref28, ref29;
    const bundlePricesDiscounted = hasDiscounts ? [
        (ref21 = (_discountedPrice = frame.price.discountedPrice) !== null && _discountedPrice !== void 0 ? _discountedPrice : frame.price.originalPrice) !== null && ref21 !== void 0 ? ref21 : 0,
        (ref23 = (ref22 = lensType === null || lensType === void 0 ? void 0 : (ref11 = lensType.price) === null || ref11 === void 0 ? void 0 : ref11.discountedPrice) !== null && ref22 !== void 0 ? ref22 : lensType === null || lensType === void 0 ? void 0 : (ref12 = lensType.price) === null || ref12 === void 0 ? void 0 : ref12.originalPrice) !== null && ref23 !== void 0 ? ref23 : 0,
        hasVisitedStepLensPackage2 ? (ref25 = (ref24 = lensPackage === null || lensPackage === void 0 ? void 0 : (ref13 = lensPackage.price) === null || ref13 === void 0 ? void 0 : ref13.discountedPrice) !== null && ref24 !== void 0 ? ref24 : lensPackage === null || lensPackage === void 0 ? void 0 : (ref14 = lensPackage.price) === null || ref14 === void 0 ? void 0 : ref14.originalPrice) !== null && ref25 !== void 0 ? ref25 : 0 : 0,
        hasVisitedStepAddOn2 ? (ref27 = (ref26 = addOn === null || addOn === void 0 ? void 0 : (ref15 = addOn.price) === null || ref15 === void 0 ? void 0 : ref15.discountedPrice) !== null && ref26 !== void 0 ? ref26 : addOn === null || addOn === void 0 ? void 0 : (ref16 = addOn.price) === null || ref16 === void 0 ? void 0 : ref16.originalPrice) !== null && ref27 !== void 0 ? ref27 : 0 : 0,
        (ref29 = (ref28 = bundleMinimumSupplement === null || bundleMinimumSupplement === void 0 ? void 0 : bundleMinimumSupplement.discountedPrice) !== null && ref28 !== void 0 ? ref28 : bundleMinimumSupplement === null || bundleMinimumSupplement === void 0 ? void 0 : bundleMinimumSupplement.originalPrice) !== null && ref29 !== void 0 ? ref29 : 0, 
    ] : [];
    const discountedPrice = hasDiscounts ? bundlePricesDiscounted.reduce((sum, current)=>sum + current
    , 0) : undefined;
    return {
        originalPrice,
        discountedPrice,
        currency: frame.price.currency
    };
});
const getVerificationAllowed = createSelector(getFrameSku, (s)=>s
, (frameSku, state)=>{
    if (!frameSku) {
        return undefined;
    }
    return getVerificationAllowed$1(state, {
        frameSku
    });
});
var ref;
/**
 * Returns a boolean whether the currently selected package allows add ons to be optional
 * The default value is always `true` unless it is specifically set to `false` in the product data
 */ const getAreAddOnsOptional = createSelector(getSelectedPackage, (p)=>{
    return (ref = p === null || p === void 0 ? void 0 : p.areAddOnsOptional) !== null && ref !== void 0 ? ref : true;
});

export { getAddOns, getAdditionalPrescriptionInfoFormState, getAreAddOnsOptional, getBookAppointmentRoute, getBundleMinimumSupplement, getBundlePrice, getFrameImage, getFrameSku, getIsExtraValidationStepEnabled, getIsPrescriptionFormStepValid, getIsPrescriptionUploadStepValid, getLensTypes, getManualPrescriptionValidationState, getPackages, getPdUploadValidationRequirement, getPrescription, getPrescriptionType, getRxEntryTypes, getSelectedAddOn, getSelectedFrame, getSelectedFrameProductType, getSelectedLensType, getSelectedPackage, getVerificationAllowed, hasVisitedStepAddOn, hasVisitedStepLensPackage, hasVisitedStepPrescription };
//# sourceMappingURL=selectors.js.map
